import {
  ArrowRightStartOnRectangleIcon,
  Bars3Icon,
} from "@heroicons/react/16/solid";
import {
  ChevronDoubleLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { Dispatch, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { resetUserId } from "@services/StateManagement/UserSlice";
import { resetAuthState } from "@services/StateManagement/AuthSlice";
import { myUserWithPasswordItems } from "./SideBarItems";
import { useKeycloak } from "@react-keycloak/web";
import { UnknownAction } from "@reduxjs/toolkit";
import { LOGIN_STATES } from "../../constants/Enums";

interface SidebarItem {
  key: string;
  title: string;
  icon: JSX.Element;
  path?: string;
  state?: any;
  children?: SidebarItem[];
}

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (arg: boolean) => void;
  items: SidebarItem[];
  currentPage: string;
}

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  items,
  currentPage,
}: SidebarProps) => {
  const { keycloak } = useKeycloak();
  const dispatch: Dispatch<UnknownAction> = useDispatch();
  const navigator = useNavigate();

  const userId: string = useSelector((state) => state.user.userId);
  const isSysAdminRole: boolean = useSelector((state) => state.auth.isAdmin);
  const isFirstUser: boolean = useSelector((state) => state.auth.isFirstUser);

  const trigger = useRef<any>(null);
  const sidebar = useRef<any>(null);

  const [expandedItems, setExpandedItems] = useState<{
    [key: string]: boolean;
  }>({});

  const toggleItem = (key: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleLogout = () => {
    dispatch(resetUserId());
    dispatch(resetAuthState());

    localStorage.removeItem("userInfo");
    localStorage.setItem("logInState", LOGIN_STATES.LOGGING_OUT);
    keycloak.logout({ redirectUri: window.location.origin });
  };

  const doNavigate = (path: string | undefined) => {
    if (path && path !== currentPage) {
      navigator(path);
    }
  };

  const renderSidebarItems = (items: SidebarItem[]) => {
    return items.map((item) => (
      <li key={item.key} className="relative">
        <div onClick={() => item.children && toggleItem(item.key)}>
          {!item.children ? (
            <div
              className="group relative flex items-center gap-2.5 rounded-sm py-2 px-4 text-bodydark1 sm:text-md md:text-md lg:text-md duration-300 ease-in-out hover:bg-graydark hover:cursor-pointer"
              onClick={() => {
                doNavigate(item.path);
              }}
            >
              {item.icon}
              {item.title}
              {item.children && (
                <span className="ml-auto">
                  {expandedItems[item.key] ? (
                    <ChevronUpIcon className="w-4 h-4" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4" />
                  )}
                </span>
              )}
            </div>
          ) : (
            <div className="pt-group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark hover:cursor-pointer">
              {item.icon}
              {item.title}
              {item.children && (
                <span className="ml-auto">
                  {expandedItems[item.key] ? (
                    <ChevronUpIcon className="w-4 h-4" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4" />
                  )}
                </span>
              )}
            </div>
          )}
        </div>
        {item.children && expandedItems[item.key] && (
          <ul className="ml-8 mt-2">{renderSidebarItems(item.children)}</ul>
        )}
      </li>
    ));
  };

  return (
    <>
      <div>
        <button
          ref={trigger}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-controls="sidebar"
          aria-expanded={sidebarOpen}
          className="lg:hidden p-4 bg-gradient-to-r bg-mayo-bg  text-white"
        >
          <div className="bg-slate-200 rounded-lg p-1 ">
            <Bars3Icon className="w-4 h-4 text-black" />
          </div>
        </button>
      </div>

      <aside
        ref={sidebar}
        className={`absolute left-0 top-0 z-9999 flex h-screen w-72 flex-col overflow-y-hidden bg-side-bar-dark duration-300 ease-linear lg:static lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div>
          <button
            ref={trigger}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
            className="block lg:hidden"
          >
            <svg
              className="fill-current ml-5 mt-5"
              width="25"
              height="25"
              viewBox="0 0 25 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <ChevronDoubleLeftIcon className="text-white"></ChevronDoubleLeftIcon>
            </svg>
          </button>
        </div>

        <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">
          <nav className="mt-5 py-4 px-4 lg:mt-9 lg:px-6">
            <ul className="mb-6 flex flex-col gap-1.5">
              {renderSidebarItems(items)}

              {isSysAdminRole === false && isFirstUser === false && (
                <hr className="border-t-2 border border-white m-4" />
              )}

              {isSysAdminRole === false &&
                isFirstUser === false &&
                renderSidebarItems(myUserWithPasswordItems(userId))}
              <hr className="border-t-2 border border-white m-4" />
              <li>
                <div
                  onClick={handleLogout}
                  className="pt-group relative flex items-center gap-2 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark hover:cursor-pointer"
                >
                  <ArrowRightStartOnRectangleIcon className="w-6 h-6" />
                  ログアウト
                </div>
              </li>
            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
